import { useState } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useTablesQuery, databaseKeys } from '@/hooks/useDatabase'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { useQueryClient } from '@tanstack/react-query'
import {
  InformationCircleIcon
} from '@heroicons/react/24/outline'

interface CreateTriggerViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onTriggerCreated: () => void
}

export function CreateTriggerView({ connection, database, schema, onTriggerCreated }: CreateTriggerViewProps) {
  const [triggerName, setTriggerName] = useState('')
  const [selectedTable, setSelectedTable] = useState('')
  const [timing, setTiming] = useState<'BEFORE' | 'AFTER' | 'INSTEAD OF'>('BEFORE')
  const [events, setEvents] = useState<string[]>(['INSERT'])
  const [triggerBody, setTriggerBody] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  
  const { data: tablesResponse } = useTablesQuery(
    connection.id,
    database,
    schema,
    { 
      enabled: true,
      databaseType: connection.type === 'postgresql' ? 'PostgreSQL' : 'MySQL'
    }
  )

  const tables = tablesResponse?.data || []

  const handleEventChange = (event: string, checked: boolean) => {
    if (checked) {
      setEvents(prev => [...prev, event])
    } else {
      setEvents(prev => prev.filter(e => e !== event))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!triggerName.trim() || !selectedTable || events.length === 0 || !triggerBody.trim()) {
      showToast('error', 'Please fill in all required fields')
      return
    }

    setIsSubmitting(true)
    
    try {
      // Build the complete trigger definition
      const eventsStr = events.join(' OR ')
      const triggerDefinition = isDatabasePostgreSQL
        ? `CREATE TRIGGER ${triggerName}
    ${timing} ${eventsStr} ON ${selectedTable}
    FOR EACH ROW
EXECUTE FUNCTION trigger_${triggerName}();

CREATE OR REPLACE FUNCTION trigger_${triggerName}()
RETURNS TRIGGER AS $$
${triggerBody}
$$ LANGUAGE plpgsql;`
        : `CREATE TRIGGER ${triggerName}
    ${timing} ${eventsStr} ON ${selectedTable}
    FOR EACH ROW
${triggerBody}`

      // Create trigger using agent-proxy
      await AgentProxyService.createTrigger(
        connection.id,
        database,
        {
          trigger_name: triggerName,
          table_name: selectedTable,
          timing: timing,
          events: events,
          trigger_definition: triggerDefinition
        },
        schema
      )
      
      showToast('success', `Trigger "${triggerName}" created successfully`)
      
      // Invalidate triggers queries to refresh UI
      await queryClient.invalidateQueries({ 
        queryKey: [...databaseKeys.all, 'triggers', connection.id, database, schema] 
      })
      
      onTriggerCreated()
    } catch (error) {
      console.error('Failed to create trigger:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create trigger'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isDatabasePostgreSQL = connection.type === 'postgresql'
  const isDatabaseMySQL = connection.type === 'mysql'

  const availableEvents = isDatabasePostgreSQL 
    ? ['INSERT', 'UPDATE', 'DELETE', 'TRUNCATE']
    : ['INSERT', 'UPDATE', 'DELETE']

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center">
          <div className="text-2xl mr-3">⚡</div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Trigger
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new database trigger that automatically executes code in response to events
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Trigger Name *
                  </label>
                  <input
                    type="text"
                    value={triggerName}
                    onChange={(e) => { setTriggerName(e.target.value) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="trg_example_name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Table *
                  </label>
                  <select
                    value={selectedTable}
                    onChange={(e) => { setSelectedTable(e.target.value) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a table</option>
                    {tables.map((table: { name?: string }) => (
                      <option key={table.name} value={table.name}>
                        {table.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Timing
                  </label>
                  <select
                    value={timing}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === 'BEFORE' || v === 'AFTER' || v === 'INSTEAD OF') {
                        setTiming(v)
                      } else {
                        setTiming('BEFORE')
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="BEFORE">BEFORE</option>
                    <option value="AFTER">AFTER</option>
                    {isDatabasePostgreSQL && <option value="INSTEAD OF">INSTEAD OF</option>}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Events *
                  </label>
                  <div className="space-y-2">
                    {availableEvents.map((event) => (
                      <label key={event} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={events.includes(event)}
                          onChange={(e) => { handleEventChange(event, e.target.checked) }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{event}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Trigger Definition */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Trigger Definition
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Trigger Body *
                </label>
                <textarea
                  value={triggerBody}
                  onChange={(e) => { setTriggerBody(e.target.value) }}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder={isDatabasePostgreSQL 
                    ? `BEGIN
    -- Your trigger logic here
    -- Use NEW and OLD to reference row data
    -- Example: NEW.updated_at = NOW();
    RETURN NEW;
END;`
                    : `BEGIN
    -- Your trigger logic here
    -- Use NEW and OLD to reference row data
    -- Example: SET NEW.updated_at = NOW();
END`
                  }
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {isDatabasePostgreSQL 
                    ? "Write the trigger function body. Use NEW and OLD to reference row data. For BEFORE triggers, you can RETURN NEW or NULL."
                    : "Write the trigger body. Use NEW and OLD to reference row data."
                  }
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Trigger Guidelines:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>BEFORE triggers can modify NEW values and prevent operations by returning NULL</li>
                    <li>AFTER triggers cannot modify row data but can perform logging or notifications</li>
                    <li>Use OLD to reference the original row data (UPDATE/DELETE)</li>
                    <li>Use NEW to reference the new row data (INSERT/UPDATE)</li>
                    {isDatabasePostgreSQL && <li>PostgreSQL triggers require a function that returns TRIGGER type</li>}
                    {isDatabaseMySQL && <li>MySQL triggers don't require explicit RETURN statements</li>}
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onTriggerCreated}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Creating...' : 'Create Trigger'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
